import {
  AlertTriangle,
  ChevronDown,
  DollarSign,
  Download,
  Globe,
  Heart,
  Loader2,
  Scale,
  Shield,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import type {
  ImpactAnalysisData,
  CriticalInfrastructureImpact,
  DefenseCapacityImpact,
  InternationalObligationsImpact,
  LegalRepresentativeData,
} from "@/types/impact-analysis";
import { cn } from "../../lib/utils";
import { ImpactResultsTable } from "@/components/impact-analysis/ImpactResultsTable";
import { LegalRepresentativeForm } from "@/components/impact-analysis/LegalRepresentativeForm";
import type { ContactData } from "../../types/contact";
import { DEFAULT_IMPACT_ANALYSIS } from "@/constants/analysis";
import { migrateOldImpactData } from "@/utils/impact-analysis";
import { calculateImpactAnalysisResult } from "@/utils/disruptiveEffectCalculator";

type ImpactAnalysisSectionProps = {
  impactAnalysis?: ImpactAnalysisData;
  reprezentantData?: ContactData["reprezentantLegal"];
  onAnalysisComplete: (
    analysis: ImpactAnalysisData,
    reprezentantData: LegalRepresentativeData
  ) => void;
  onSkipAnalysis: () => void;
  onDownloadPdf?: (
    analysis: ImpactAnalysisData,
    reprezentantData: LegalRepresentativeData
  ) => void;
  isProcessing?: boolean;
  hasInsufficientCredits?: boolean;
  hasInsufficientClassifCredits?: boolean;
  isLoadingCredits?: boolean;
};

export function ImpactAnalysisSection({
  impactAnalysis,
  onAnalysisComplete,
  reprezentantData: initialReprezentantData,
  onDownloadPdf,
  isProcessing,
  hasInsufficientCredits,
  hasInsufficientClassifCredits,
  isLoadingCredits,
}: ImpactAnalysisSectionProps) {
  const [impactData, setImpactData] = useState<ImpactAnalysisData>(
    migrateOldImpactData(impactAnalysis) || DEFAULT_IMPACT_ANALYSIS
  );

  const [sectionStates, setSectionStates] = useState({
    section1: true,
    section2: false,
    section3: false,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const [reprezentantData, setReprezentantData] =
    useState<LegalRepresentativeData>({
      nume: initialReprezentantData?.nume || "",
      prenume: initialReprezentantData?.prenume || "",
      functia: initialReprezentantData?.functie || "",
    });

  useEffect(() => {
    const newResult = calculateImpactAnalysisResult(impactData);
    setImpactData((prev) => ({
      ...prev,
      result: newResult,
    }));
  }, [
    impactData.section1_mandatory,
    impactData.section2_public_impact,
    impactData.section3_systemic_risk,
  ]);

  const handleReprezentantChange = (
    field: keyof LegalRepresentativeData,
    value: string
  ) => {
    setReprezentantData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user provides input
    const errorKey = `repr-${field}`;
    if (fieldErrors[errorKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleRadioChange = <T extends boolean | string>(
    section: keyof ImpactAnalysisData,
    subsection: string,
    field: string,
    value: T,
    fieldsToClear?: string[]
  ) => {
    const shouldClearExplanation = value === false || value === "no";
    const fieldsToReset: Record<string, unknown> = {};

    if (shouldClearExplanation) {
      fieldsToReset[`${field}_explanation`] = "";
    }

    if (fieldsToClear) {
      for (const f of fieldsToClear) {
        fieldsToReset[f] = f.includes("explanation") ? "" : null;
      }
    }

    if (subsection) {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            [field]: value,
            ...fieldsToReset,
          },
        },
      }));
    } else {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
          ...fieldsToReset,
        },
      }));
    }

    // Clear field error when user provides input
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumberChange = (
    section: keyof ImpactAnalysisData,
    subsection: string,
    field: string,
    value: number
  ) => {
    if (subsection) {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            [field]: value,
            // Clear explanation when value is 0
            ...(value === 0 && { [`${field}_explanation`]: "" }),
          },
        },
      }));
    } else {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
          // Clear explanation when value is 0
          ...(value === 0 && { [`${field}_explanation`]: "" }),
        },
      }));
    }

    // Clear field error when user provides input
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTextareaChange = (
    section: keyof ImpactAnalysisData,
    subsection: string,
    field: string,
    value: string
  ) => {
    if (subsection) {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            [field]: value,
          },
        },
      }));
    } else {
      setImpactData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    }

    // Clear field error when user provides input
    const errorKey = `${field}_explanation`;
    if (fieldErrors[errorKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateRequiredFields = (): {
    isValid: boolean;
    fieldErrors: Record<string, string>;
  } => {
    const errors: Record<string, string> = {};

    // Cache nested objects for performance
    const section1 = impactData.section1_mandatory;
    const fundamentalRights =
      impactData.section2_public_impact.fundamental_rights;
    const nationalEconomy = impactData.section2_public_impact.national_economy;
    const healthLife = impactData.section2_public_impact.health_life;
    const financialImpact = impactData.section2_public_impact.financial_impact;
    const defenseSecurity = impactData.section2_public_impact.defense_security;
    const section3 = impactData.section3_systemic_risk;

    // Error message constants
    const MSG_REQ = "Acest câmp este obligatoriu";
    const MSG_YES = "Explicația este obligatorie dacă ați selectat 'Da'";
    const MSG_NUM =
      "Explicația este obligatorie dacă ați introdus o valoare diferită de '0'";
    const MSG_VAL = "Explicația este obligatorie dacă ați introdus o valoare";

    // Cache conditional check
    const skipSections23 = section1.is_sole_essential_provider === true;

    // Section 1 - Mandatory fields
    if (section1.is_sole_essential_provider === null) {
      errors["is_sole_essential_provider"] = MSG_REQ;
    }
    if (section1.formally_designated === null) {
      errors["formally_designated"] = MSG_REQ;
    }

    // Section 2 and 3 are optional if the first question is answered "Da"
    if (!skipSections23) {
      // Section 2 - Public Impact - Required fields
      if (fundamentalRights.personal_data_affected === null) {
        errors["personal_data_affected"] = MSG_REQ;
      }
      if (fundamentalRights.public_service_access_lost === null) {
        errors["public_service_access_lost"] = MSG_REQ;
      }
      if (nationalEconomy.gdp_loss_over_threshold === null) {
        errors["gdp_loss_over_threshold"] = MSG_REQ;
      }
      if (nationalEconomy.affects_other_sector_services === null) {
        errors["affects_other_sector_services"] = MSG_REQ;
      }
      if (nationalEconomy.affects_critical_infrastructure === null) {
        errors["affects_critical_infrastructure"] = MSG_REQ;
      }
      if (healthLife.can_cause_death === null) {
        errors["can_cause_death"] = MSG_REQ;
      }
      if (healthLife.chronic_illness_or_disability === null) {
        errors["chronic_illness_or_disability"] = MSG_REQ;
      }
      if (healthLife.traumatic_injuries_or_health_impact === null) {
        errors["traumatic_injuries_or_health_impact"] = MSG_REQ;
      }
      if (financialImpact.people_financial_loss === null) {
        errors["people_financial_loss"] = MSG_REQ;
      }
      if (defenseSecurity.affects_defense_capacity === null) {
        errors["affects_defense_capacity"] = MSG_REQ;
      }
      if (defenseSecurity.generates_state_incapacity === null) {
        errors["generates_state_incapacity"] = MSG_REQ;
      }
      if (defenseSecurity.damages_state_reputation === null) {
        errors["damages_state_reputation"] = MSG_REQ;
      }

      // Section 3 - Systemic Risk - Required fields
      if (section3.affects_other_sectors === null) {
        errors["affects_other_sectors"] = MSG_REQ;
      }
      if (section3.affects_international_obligations === null) {
        errors["affects_international_obligations"] = MSG_REQ;
      }
    }

    // Conditional validation for explanation fields when "Da" is selected
    // Section 1
    if (
      section1.is_sole_essential_provider === true &&
      !section1.is_sole_essential_provider_explanation?.trim()
    ) {
      errors["is_sole_essential_provider_explanation"] = MSG_YES;
    }
    if (
      section1.formally_designated === true &&
      !section1.formally_designated_explanation?.trim()
    ) {
      errors["formally_designated_explanation"] = MSG_YES;
    }

    // Section 2 and 3 explanation validations are optional if the first question is answered "Da"
    if (!skipSections23) {
      // Section 2 - National Economy
      if (
        nationalEconomy.gdp_loss_over_threshold === true &&
        !nationalEconomy.gdp_loss_over_threshold_explanation?.trim()
      ) {
        errors["gdp_loss_over_threshold_explanation"] = MSG_YES;
      }
      if (
        nationalEconomy.affects_other_sector_services === true &&
        !nationalEconomy.affects_other_sector_services_explanation?.trim()
      ) {
        errors["affects_other_sector_services_explanation"] = MSG_YES;
      }
      if (
        (nationalEconomy.affects_critical_infrastructure === "affected" ||
          nationalEconomy.affects_critical_infrastructure === "destroyed") &&
        !nationalEconomy.affects_critical_infrastructure_explanation?.trim()
      ) {
        errors["affects_critical_infrastructure_explanation"] = MSG_YES;
      }

      // Section 2 - Health and Life
      if (
        healthLife.can_cause_death === true &&
        !healthLife.can_cause_death_explanation?.trim()
      ) {
        errors["can_cause_death_explanation"] = MSG_YES;
      }

      // Section 2 - Defense and Security
      if (
        (defenseSecurity.affects_defense_capacity === "affected" ||
          defenseSecurity.affects_defense_capacity === "destroyed") &&
        !defenseSecurity.affects_defense_capacity_explanation?.trim()
      ) {
        errors["affects_defense_capacity_explanation"] = MSG_YES;
      }
      if (
        defenseSecurity.generates_state_incapacity === true &&
        !defenseSecurity.generates_state_incapacity_explanation?.trim()
      ) {
        errors["generates_state_incapacity_explanation"] = MSG_YES;
      }
      if (
        defenseSecurity.damages_state_reputation === true &&
        !defenseSecurity.damages_state_reputation_explanation?.trim()
      ) {
        errors["damages_state_reputation_explanation"] = MSG_YES;
      }

      // Section 3 - Systemic Risk
      if (
        section3.affects_other_sectors === true &&
        !section3.affects_other_sectors_explanation?.trim()
      ) {
        errors["affects_other_sectors_explanation"] = MSG_YES;
      }
      if (
        (section3.affects_international_obligations === "affected" ||
          section3.affects_international_obligations === "destroyed") &&
        !section3.affects_international_obligations_explanation?.trim()
      ) {
        errors["affects_international_obligations_explanation"] = MSG_YES;
      }
    }

    // Conditional validation for number fields (when value is not 0)
    // Section 2 and 3 number field validations are optional if the first question is answered "Da"
    if (!skipSections23) {
      if (
        fundamentalRights.personal_data_affected !== null &&
        fundamentalRights.personal_data_affected > 0 &&
        !fundamentalRights.personal_data_affected_explanation?.trim()
      ) {
        errors["personal_data_affected_explanation"] = MSG_NUM;
      }
      if (
        fundamentalRights.public_service_access_lost !== null &&
        fundamentalRights.public_service_access_lost > 0 &&
        !fundamentalRights.public_service_access_lost_explanation?.trim()
      ) {
        errors["public_service_access_lost_explanation"] = MSG_NUM;
      }
      if (
        healthLife.chronic_illness_or_disability !== null &&
        healthLife.chronic_illness_or_disability > 0 &&
        !healthLife.chronic_illness_or_disability_explanation?.trim()
      ) {
        errors["chronic_illness_or_disability_explanation"] = MSG_NUM;
      }
      if (
        healthLife.traumatic_injuries_or_health_impact !== null &&
        healthLife.traumatic_injuries_or_health_impact > 0 &&
        !healthLife.traumatic_injuries_or_health_impact_explanation?.trim()
      ) {
        errors["traumatic_injuries_or_health_impact_explanation"] = MSG_NUM;
      }
      if (
        financialImpact.people_financial_loss !== null &&
        financialImpact.people_financial_loss > 0 &&
        !financialImpact.people_financial_loss_explanation?.trim()
      ) {
        errors["people_financial_loss_explanation"] = MSG_NUM;
      }
      if (
        nationalEconomy.highest_sector_impact_percentage !== null &&
        nationalEconomy.highest_sector_impact_percentage > 0 &&
        !nationalEconomy.highest_sector_impact_percentage_explanation?.trim()
      ) {
        errors["highest_sector_impact_percentage_explanation"] = MSG_VAL;
      }
      if (
        section3.highest_impact_percentage !== null &&
        section3.highest_impact_percentage > 0 &&
        !section3.highest_impact_percentage_explanation?.trim()
      ) {
        errors["highest_impact_percentage_explanation"] = MSG_VAL;
      }
    }

    // Reprezentant entitate - All fields are required
    if (!reprezentantData.prenume?.trim()) {
      errors["repr-prenume"] = MSG_REQ;
    }
    if (!reprezentantData.nume?.trim()) {
      errors["repr-nume"] = MSG_REQ;
    }
    if (!reprezentantData.functia?.trim()) {
      errors["repr-functia"] = MSG_REQ;
    }

    return { isValid: Object.keys(errors).length === 0, fieldErrors: errors };
  };

  const validateForm = (): boolean => {
    setFieldErrors({});
    const validation = validateRequiredFields();

    if (!validation.isValid) {
      setFieldErrors(validation.fieldErrors);
      const errorCount = Object.keys(validation.fieldErrors).length;
      toast({
        title: "Completare incompletă",
        description: `Vă rugăm să completați toate câmpurile obligatorii. Au fost găsite ${errorCount} erori.`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const processAnalysis = () => {
    if (!validateForm()) return;

    toast({
      title: "Analiză completată cu succes",
      description: "Analiza de impact a fost salvată și procesată.",
    });
    onAnalysisComplete(impactData, reprezentantData);
  };

  const processDownloadPdf = () => {
    if (!onDownloadPdf || !validateForm()) return;
    onDownloadPdf(impactData, reprezentantData);
  };

  const toggleSection = (section: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper component for displaying field errors
  const FieldError = ({ fieldName }: { fieldName: string }) => {
    const error = fieldErrors[fieldName];
    if (!error) return null;

    return (
      <div className="flex items-center gap-2 mt-1">
        <AlertTriangle className="h-4 w-4 text-destructive" />
        <span className="text-sm text-destructive">{error}</span>
      </div>
    );
  };

  return (
    <div className="md:space-y-6 space-y-4">
      <div className="md:space-y-6 space-y-4">
        {/* Section 1: Mandatory (9a and 9d) */}
        <Card className="border-accent/20">
          <Collapsible
            open={sectionStates.section1}
            onOpenChange={() => toggleSection("section1")}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-accent/5">
                <CardTitle className="text-sm md:text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    Secțiunea 1: Evaluarea aplicabilității art. 9 lit. a) și d)
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${sectionStates.section1 ? "rotate-180" : ""}`}
                  />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="py-3 space-y-4 md:py-3">
                <div className="space-y-3">
                  <Label className="text-sm md:text-base font-medium">
                    1. Entitatea este singurul furnizor al unui serviciu care
                    este esențial pentru susținerea unor activități societale și
                    economice critice?
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <RadioGroup
                    value={
                      impactData.section1_mandatory.is_sole_essential_provider?.toString() ||
                      ""
                    }
                    onValueChange={(value) =>
                      handleRadioChange(
                        "section1_mandatory",
                        "",
                        "is_sole_essential_provider",
                        value === "true"
                      )
                    }
                    className={
                      fieldErrors["is_sole_essential_provider"]
                        ? "border border-destructive rounded-md p-2"
                        : ""
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="true"
                        id="sole-provider-da"
                        variant="checkbox"
                      />
                      <Label
                        className="cursor-pointer"
                        htmlFor="sole-provider-da"
                      >
                        Da
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="false"
                        id="sole-provider-nu"
                        variant="checkbox"
                      />
                      <Label
                        className="cursor-pointer"
                        htmlFor="sole-provider-nu"
                      >
                        Nu
                      </Label>
                    </div>
                  </RadioGroup>
                  <FieldError fieldName="is_sole_essential_provider" />
                  {impactData.section1_mandatory.is_sole_essential_provider ===
                    true && (
                      <div className="space-y-2">
                        <Label className="text-sm md:text-base">
                          Explicație{" "}
                          <span className="text-destructive ml-1">*</span>
                        </Label>
                        <Textarea
                          placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                          value={
                            impactData.section1_mandatory
                              .is_sole_essential_provider_explanation || ""
                          }
                          onChange={(e) =>
                            handleTextareaChange(
                              "section1_mandatory",
                              "",
                              "is_sole_essential_provider_explanation",
                              e.target.value
                            )
                          }
                          className={cn(
                            fieldErrors["is_sole_essential_provider_explanation"]
                              ? "border-destructive"
                              : "",
                            "text-sm md:text-base"
                          )}
                        />
                        <FieldError fieldName="is_sole_essential_provider_explanation" />
                      </div>
                    )}
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm md:text-base font-medium">
                    2. A fost entitatea dumneavoastră desemnată formal
                    (printr-un act normativ sau administrativ) ca fiind entitate
                    ce furnizează servicii care depind de infrastructuri de
                    importanță critică la nivel național, conform Legii nr.
                    163/2021 sau alt act normativ relevant?
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <RadioGroup
                    value={
                      impactData.section1_mandatory.formally_designated?.toString() ||
                      ""
                    }
                    onValueChange={(value) =>
                      handleRadioChange(
                        "section1_mandatory",
                        "",
                        "formally_designated",
                        value === "true"
                      )
                    }
                    className={
                      fieldErrors["formally_designated"]
                        ? "border border-destructive rounded-md p-2"
                        : ""
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="true"
                        id="critical-da"
                        variant="checkbox"
                      />
                      <Label className="cursor-pointer" htmlFor="critical-da">
                        Da
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="false"
                        id="critical-nu"
                        variant="checkbox"
                      />
                      <Label className="cursor-pointer" htmlFor="critical-nu">
                        Nu
                      </Label>
                    </div>
                  </RadioGroup>
                  <FieldError fieldName="formally_designated" />
                  {impactData.section1_mandatory.formally_designated ===
                    true && (
                      <div className="space-y-2">
                        <Label className="text-sm md:text-base">
                          Explicație{" "}
                          <span className="text-destructive ml-1">*</span>
                        </Label>
                        <Textarea
                          placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                          value={
                            impactData.section1_mandatory
                              .formally_designated_explanation || ""
                          }
                          onChange={(e) =>
                            handleTextareaChange(
                              "section1_mandatory",
                              "",
                              "formally_designated_explanation",
                              e.target.value
                            )
                          }
                          className={cn(
                            fieldErrors["formally_designated_explanation"]
                              ? "border-destructive"
                              : "",
                            "text-sm md:text-base"
                          )}
                        />
                        <FieldError fieldName="formally_designated_explanation" />
                      </div>
                    )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Conditional alert when first question is answered "Da" */}
        {impactData.section1_mandatory.is_sole_essential_provider === true && (
          <Alert className="border-destructive/20 bg-destructive/5">
            <AlertTriangle className="h-4 w-4 !text-destructive" />
            <AlertDescription className="text-sm text-destructive ml-2">
              Având în vedere răspunsurile la întrebările de mai sus,
              continuarea autoevaluării este opțională.
            </AlertDescription>
          </Alert>
        )}

        {/* Section 2: Public Impact (9b) */}
        <Card className="border-warning/20">
          <Collapsible
            open={sectionStates.section2}
            onOpenChange={() => toggleSection("section2")}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-warning/5">
                <CardTitle className="text-sm md:text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    Secțiunea 2: Evaluarea aplicabilității art. 9 lit. b)
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${sectionStates.section2 ? "rotate-180" : ""}`}
                  />
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  În vederea determinării categoriei de impact pe care entitatea
                  îl poate genera, aceasta trebuie să ia în considerare efectul
                  potențial maxim al compromiterii, blocării ori distrugerii
                  complete a datelor, serviciului sau infrastructurii entității.
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="py-3 space-y-6 md:py-3">
                {/* Subsection A: Fundamental Rights */}
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Impactul asupra drepturilor și libertăților fundamentale
                  </h4>
                  <div className="space-y-3 ml-6">
                    <div className="space-y-2">
                      <Label className="text-sm md:text-base">
                        1. Care este numărul maxim de persoane fizice (clienți,
                        pacienți, angajați etc.) ale căror date cu caracter
                        personal le gestionați în format electronic?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Inserați valoarea"
                        value={
                          impactData.section2_public_impact.fundamental_rights
                            .personal_data_affected ?? ""
                        }
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty string or valid numbers >= 0
                          if (
                            inputValue === "" ||
                            inputValue === "0" ||
                            (parseInt(inputValue) >= 0 &&
                              !isNaN(parseInt(inputValue)))
                          ) {
                            const value =
                              inputValue === "" ? 0 : parseInt(inputValue);
                            handleNumberChange(
                              "section2_public_impact",
                              "fundamental_rights",
                              "personal_data_affected",
                              value
                            );
                          }
                        }}
                        className={cn(
                          fieldErrors["personal_data_affected"]
                            ? "border-destructive"
                            : "",
                          "text-sm md:text-base"
                        )}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Se va insera o valoare numerică fără spații sau puncte
                        de delimitare a miilor, milioanelor etc. Dacă nu se
                        aplică entității introduceți „0"
                      </p>
                      <FieldError fieldName="personal_data_affected" />
                      {impactData.section2_public_impact.fundamental_rights
                        .personal_data_affected !== null &&
                        impactData.section2_public_impact.fundamental_rights
                          .personal_data_affected > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                              value={
                                impactData.section2_public_impact
                                  .fundamental_rights
                                  .personal_data_affected_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "fundamental_rights",
                                  "personal_data_affected_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "personal_data_affected_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="personal_data_affected_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm md:text-base">
                        2. Care este numărul maxim de persoane cărora le-ar
                        putea fi afectat accesul la un serviciu public esențial
                        (e.g. întrerupere sau alt mod de afectare a modului de
                        funcționare a serviciilor)?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Inserați valoarea"
                        value={
                          impactData.section2_public_impact.fundamental_rights
                            .public_service_access_lost ?? ""
                        }
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty string or valid numbers >= 0
                          if (
                            inputValue === "" ||
                            inputValue === "0" ||
                            (parseInt(inputValue) >= 0 &&
                              !isNaN(parseInt(inputValue)))
                          ) {
                            const value =
                              inputValue === "" ? 0 : parseInt(inputValue);
                            handleNumberChange(
                              "section2_public_impact",
                              "fundamental_rights",
                              "public_service_access_lost",
                              value
                            );
                          }
                        }}
                        className={cn(
                          fieldErrors["public_service_access_lost"]
                            ? "border-destructive"
                            : "",
                          "text-sm md:text-base"
                        )}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Se va insera o valoare numerică fără spații sau puncte
                        de delimitare a miilor, milioanelor etc. Dacă nu se
                        aplică entității introduceți „0"
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Lista serviciilor publice esențiale se regăsește în:{" "}
                        <a
                          href="https://legislatie.just.ro/Public/DetaliiDocument/151041"
                          target="_blank"
                          className="text-ds-blue-600"
                        >
                          Hotărârea Guvernului nr. 647/2013 privind stabilirea
                          serviciilor publice esențiale la nivelul unităților
                          administrativ-teritoriale
                        </a>
                      </p>
                      <FieldError fieldName="public_service_access_lost" />
                      {impactData.section2_public_impact.fundamental_rights
                        .public_service_access_lost !== null &&
                        impactData.section2_public_impact.fundamental_rights
                          .public_service_access_lost > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                              value={
                                impactData.section2_public_impact
                                  .fundamental_rights
                                  .public_service_access_lost_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "fundamental_rights",
                                  "public_service_access_lost_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "public_service_access_lost_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="public_service_access_lost_explanation" />
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Subsection B: National Economy */}
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Impactul asupra economiei naționale
                  </h4>
                  <div className="space-y-4 ml-6">
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        1. O întrerupere completă a serviciului furnizat poate
                        provoca pierderi la nivel național de peste 0,1% din
                        PIB-ul României?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.national_economy.gdp_loss_over_threshold?.toString() ??
                          ""
                        }
                        onValueChange={(value) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "national_economy",
                            "gdp_loss_over_threshold",
                            value === "true"
                          )
                        }
                        className={cn(
                          fieldErrors["gdp_loss_over_threshold"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="true"
                            id="gdp-da"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="gdp-da">
                            Da
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="false"
                            id="gdp-nu"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="gdp-nu">
                            Nu
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="gdp_loss_over_threshold" />
                      {impactData.section2_public_impact.national_economy
                        .gdp_loss_over_threshold === true && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.national_economy
                                  .gdp_loss_over_threshold_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "national_economy",
                                  "gdp_loss_over_threshold_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors["gdp_loss_over_threshold_explanation"]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="gdp_loss_over_threshold_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        2. Poate fi afectată furnizarea serviciilor prestate de
                        alte entități care activează în unul din sectoarele
                        prevăzute de anexele nr. 1 și 2 din OUG nr. 155/2024?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.national_economy.affects_other_sector_services?.toString() ??
                          ""
                        }
                        onValueChange={(value) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "national_economy",
                            "affects_other_sector_services",
                            value === "true",
                            [
                              "highest_sector_impact_percentage",
                              "highest_sector_impact_percentage_explanation",
                            ]
                          )
                        }
                        className={cn(
                          fieldErrors["affects_other_sector_services"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="true"
                            id="other-sector-da"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="other-sector-da"
                          >
                            Da
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="false"
                            id="other-sector-nu"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="other-sector-nu"
                          >
                            Nu
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="affects_other_sector_services" />
                      {impactData.section2_public_impact.national_economy
                        .affects_other_sector_services === true && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.national_economy
                                  .affects_other_sector_services_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "national_economy",
                                  "affects_other_sector_services_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "affects_other_sector_services_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="affects_other_sector_services_explanation" />
                          </div>
                        )}

                      {impactData.section2_public_impact.national_economy
                        .affects_other_sector_services && (
                          <div className="ml-4 space-y-2">
                            <Label className="text-sm md:text-base">
                              Dacă Da, care este procentul cel mai mare de
                              afectare a unui sector?
                            </Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="Inserați valoarea"
                              value={
                                impactData.section2_public_impact.national_economy
                                  .highest_sector_impact_percentage ?? ""
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                // Allow empty string or valid numbers 0-100
                                if (
                                  inputValue === "" ||
                                  inputValue === "0" ||
                                  (parseInt(inputValue) >= 0 &&
                                    parseInt(inputValue) <= 100 &&
                                    !isNaN(parseInt(inputValue)))
                                ) {
                                  const numValue =
                                    inputValue === "" ? 0 : parseInt(inputValue);
                                  setImpactData((prev) => ({
                                    ...prev,
                                    section2_public_impact: {
                                      ...prev.section2_public_impact,
                                      national_economy: {
                                        ...prev.section2_public_impact
                                          .national_economy,
                                        highest_sector_impact_percentage:
                                          numValue,
                                        // Clear explanation when value is 0
                                        ...(numValue === 0 && {
                                          highest_sector_impact_percentage_explanation:
                                            "",
                                        }),
                                      },
                                    },
                                  }));

                                  // Clear field error when user provides input
                                  if (
                                    fieldErrors[
                                    "highest_sector_impact_percentage"
                                    ]
                                  ) {
                                    setFieldErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors[
                                        "highest_sector_impact_percentage"
                                      ];
                                      return newErrors;
                                    });
                                  }
                                }
                              }}
                              className={cn(
                                fieldErrors["highest_sector_impact_percentage"]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Se va insera o valoare numerică fără spații sau
                              puncte de delimitare a miilor, milioanelor etc. Dacă
                              nu se aplică entității introduceți „0"
                            </p>
                            <FieldError fieldName="highest_sector_impact_percentage" />
                            {impactData.section2_public_impact.national_economy
                              .highest_sector_impact_percentage !== null &&
                              impactData.section2_public_impact.national_economy
                                .highest_sector_impact_percentage > 0 && (
                                <div className="space-y-2">
                                  <Label className="text-sm md:text-base">
                                    Explicație{" "}
                                    <span className="text-destructive ml-1">
                                      *
                                    </span>
                                  </Label>
                                  <Textarea
                                    placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                                    value={
                                      impactData.section2_public_impact
                                        .national_economy
                                        .highest_sector_impact_percentage_explanation ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      handleTextareaChange(
                                        "section2_public_impact",
                                        "national_economy",
                                        "highest_sector_impact_percentage_explanation",
                                        e.target.value
                                      )
                                    }
                                    className={cn(
                                      fieldErrors[
                                        "highest_sector_impact_percentage_explanation"
                                      ]
                                        ? "border-destructive"
                                        : "",
                                      "text-sm md:text-base"
                                    )}
                                  />
                                  <FieldError fieldName="highest_sector_impact_percentage_explanation" />
                                </div>
                              )}
                          </div>
                        )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        3. Poate fi afectată sau pot fi provocate distrugeri la
                        nivelul unei infrastructuri critice?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.national_economy
                            .affects_critical_infrastructure ?? ""
                        }
                        onValueChange={(value: CriticalInfrastructureImpact) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "national_economy",
                            "affects_critical_infrastructure",
                            value
                          )
                        }
                        className={cn(
                          fieldErrors["affects_critical_infrastructure"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="no"
                            id="infra-no"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="infra-no">
                            Nu
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="affected"
                            id="infra-affected"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="infra-affected"
                          >
                            Da, poate fi afectată
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="destroyed"
                            id="infra-destroyed"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="infra-destroyed"
                          >
                            Da, pot rezulta distrugeri
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="affects_critical_infrastructure" />
                      {(impactData.section2_public_impact.national_economy
                        .affects_critical_infrastructure === "affected" ||
                        impactData.section2_public_impact.national_economy
                          .affects_critical_infrastructure === "destroyed") && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 'Da'):"
                              value={
                                impactData.section2_public_impact.national_economy
                                  .affects_critical_infrastructure_explanation ||
                                ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "national_economy",
                                  "affects_critical_infrastructure_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "affects_critical_infrastructure_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="affects_critical_infrastructure_explanation" />
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Subsection C: Health and Life */}
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    Impactul asupra sănătății și vieții persoanelor
                  </h4>
                  <div className="space-y-3 ml-6">
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        1. Poate rezulta decesul unei persoane?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.health_life.can_cause_death?.toString() ??
                          ""
                        }
                        onValueChange={(value) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "health_life",
                            "can_cause_death",
                            value === "true"
                          )
                        }
                        className={cn(
                          fieldErrors["can_cause_death"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="true"
                            id="death-da"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="death-da">
                            Da
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="false"
                            id="death-nu"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="death-nu">
                            Nu
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="can_cause_death" />
                      {impactData.section2_public_impact.health_life
                        .can_cause_death === true && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.health_life
                                  .can_cause_death_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "health_life",
                                  "can_cause_death_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors["can_cause_death_explanation"]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="can_cause_death_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm md:text-base">
                        2. Care este numărul maxim de persoane care ar putea
                        suferi o îmbolnăvire cronică sau o infirmitate?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Inserați valoarea"
                        value={
                          impactData.section2_public_impact.health_life
                            .chronic_illness_or_disability ?? ""
                        }
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty string or valid numbers >= 0
                          if (
                            inputValue === "" ||
                            inputValue === "0" ||
                            (parseInt(inputValue) >= 0 &&
                              !isNaN(parseInt(inputValue)))
                          ) {
                            const value =
                              inputValue === "" ? 0 : parseInt(inputValue);
                            handleNumberChange(
                              "section2_public_impact",
                              "health_life",
                              "chronic_illness_or_disability",
                              value
                            );
                          }
                        }}
                        className={cn(
                          fieldErrors["chronic_illness_or_disability"]
                            ? "border-destructive"
                            : "",
                          "text-sm md:text-base"
                        )}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Se va insera o valoare numerică fără spații sau puncte
                        de delimitare a miilor, milioanelor etc. Dacă nu se
                        aplică entității introduceți „0"
                      </p>
                      <FieldError fieldName="chronic_illness_or_disability" />
                      {impactData.section2_public_impact.health_life
                        .chronic_illness_or_disability !== null &&
                        impactData.section2_public_impact.health_life
                          .chronic_illness_or_disability > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                              value={
                                impactData.section2_public_impact.health_life
                                  .chronic_illness_or_disability_explanation ||
                                ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "health_life",
                                  "chronic_illness_or_disability_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "chronic_illness_or_disability_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="chronic_illness_or_disability_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm md:text-base">
                        3. Care este numărul maxim de persoane care ar putea
                        suferi leziuni traumatice sau a căror sănătate ar putea
                        fi afectată?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Inserați valoarea"
                        value={
                          impactData.section2_public_impact.health_life
                            .traumatic_injuries_or_health_impact ?? ""
                        }
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty string or valid numbers >= 0
                          if (
                            inputValue === "" ||
                            inputValue === "0" ||
                            (parseInt(inputValue) >= 0 &&
                              !isNaN(parseInt(inputValue)))
                          ) {
                            const value =
                              inputValue === "" ? 0 : parseInt(inputValue);
                            handleNumberChange(
                              "section2_public_impact",
                              "health_life",
                              "traumatic_injuries_or_health_impact",
                              value
                            );
                          }
                        }}
                        className={cn(
                          fieldErrors["traumatic_injuries_or_health_impact"]
                            ? "border-destructive"
                            : "",
                          "text-sm md:text-base"
                        )}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Se va insera o valoare numerică fără spații sau puncte
                        de delimitare a miilor, milioanelor etc. Dacă nu se
                        aplică entității introduceți „0"
                      </p>
                      <FieldError fieldName="traumatic_injuries_or_health_impact" />
                      {impactData.section2_public_impact.health_life
                        .traumatic_injuries_or_health_impact !== null &&
                        impactData.section2_public_impact.health_life
                          .traumatic_injuries_or_health_impact > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                              value={
                                impactData.section2_public_impact.health_life
                                  .traumatic_injuries_or_health_impact_explanation ||
                                ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "health_life",
                                  "traumatic_injuries_or_health_impact_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "traumatic_injuries_or_health_impact_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="traumatic_injuries_or_health_impact_explanation" />
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Subsection D: Financial Impact */}
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Impactul financiar
                  </h4>
                  <div className="space-y-2 ml-6">
                    <Label className="text-sm md:text-base">
                      1. Care este numărul maxim de persoane pentru care ar
                      putea fi generată o pierdere financiară individuală de cel
                      puțin 2.500 lei?
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Inserați valoarea"
                      value={
                        impactData.section2_public_impact.financial_impact
                          .people_financial_loss ?? ""
                      }
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow empty string or valid numbers >= 0
                        if (
                          inputValue === "" ||
                          inputValue === "0" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          const value =
                            inputValue === "" ? 0 : parseInt(inputValue);
                          handleNumberChange(
                            "section2_public_impact",
                            "financial_impact",
                            "people_financial_loss",
                            value
                          );
                        }
                      }}
                      className={cn(
                        fieldErrors["people_financial_loss"]
                          ? "border-destructive"
                          : "",
                        "text-sm md:text-base"
                      )}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Se va insera o valoare numerică fără spații sau puncte de
                      delimitare a miilor, milioanelor etc. Dacă nu se aplică
                      entității introduceți „0"
                    </p>
                    <FieldError fieldName="people_financial_loss" />
                    {impactData.section2_public_impact.financial_impact
                      .people_financial_loss !== null &&
                      impactData.section2_public_impact.financial_impact
                        .people_financial_loss > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm md:text-base">
                            Explicație{" "}
                            <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Textarea
                            placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                            value={
                              impactData.section2_public_impact.financial_impact
                                .people_financial_loss_explanation || ""
                            }
                            onChange={(e) =>
                              handleTextareaChange(
                                "section2_public_impact",
                                "financial_impact",
                                "people_financial_loss_explanation",
                                e.target.value
                              )
                            }
                            className={cn(
                              fieldErrors["people_financial_loss_explanation"]
                                ? "border-destructive"
                                : "",
                              "text-sm md:text-base"
                            )}
                          />
                          <FieldError fieldName="people_financial_loss_explanation" />
                        </div>
                      )}
                  </div>
                </div>

                <Separator />

                {/* Subsection E: Defense and Security */}
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Impactul asupra apărării, ordinii publice și securității
                    naționale
                  </h4>
                  <div className="space-y-4 ml-6">
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        1. Poate fi afectată capacitatea statului de a asigura
                        apărarea, ordinea publică și securitatea națională?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.defense_security
                            .affects_defense_capacity ?? ""
                        }
                        onValueChange={(value: DefenseCapacityImpact) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "defense_security",
                            "affects_defense_capacity",
                            value
                          )
                        }
                        className={cn(
                          fieldErrors["affects_defense_capacity"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="no"
                            id="defense-no"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="defense-no"
                          >
                            Nu
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="affected"
                            id="defense-affected"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="defense-affected"
                          >
                            Da, poate fi afectată doar capacitatea de a derula
                            anumite activități din această sferă, cu consecințe
                            potențial grave.
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="destroyed"
                            id="defense-destroyed"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="defense-destroyed"
                          >
                            Da, poate fi afectată total capacitatea statului de
                            a asigura cel puțin una din atribuțiile din această
                            sferă.
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="affects_defense_capacity" />
                      {(impactData.section2_public_impact.defense_security
                        .affects_defense_capacity === "affected" ||
                        impactData.section2_public_impact.defense_security
                          .affects_defense_capacity === "destroyed") && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.defense_security
                                  .affects_defense_capacity_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "defense_security",
                                  "affects_defense_capacity_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "affects_defense_capacity_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="affects_defense_capacity_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        2. Poate fi generată incapacitatea statului de a
                        îndeplini funcțiile sale principale?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.defense_security.generates_state_incapacity?.toString() ??
                          ""
                        }
                        onValueChange={(value) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "defense_security",
                            "generates_state_incapacity",
                            value === "true"
                          )
                        }
                        className={cn(
                          fieldErrors["generates_state_incapacity"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="true"
                            id="incapacity-da"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="incapacity-da"
                          >
                            Da
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="false"
                            id="incapacity-nu"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="incapacity-nu"
                          >
                            Nu
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="generates_state_incapacity" />
                      {impactData.section2_public_impact.defense_security
                        .generates_state_incapacity === true && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.defense_security
                                  .generates_state_incapacity_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "defense_security",
                                  "generates_state_incapacity_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "generates_state_incapacity_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="generates_state_incapacity_explanation" />
                          </div>
                        )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm md:text-base">
                        3. Poate fi generat un prejudiciu grav în ceea ce
                        privește reputația statului ori încrederii cetățenilor
                        în acesta?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section2_public_impact.defense_security.damages_state_reputation?.toString() ??
                          ""
                        }
                        onValueChange={(value) =>
                          handleRadioChange(
                            "section2_public_impact",
                            "defense_security",
                            "damages_state_reputation",
                            value === "true"
                          )
                        }
                        className={cn(
                          fieldErrors["damages_state_reputation"]
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="true"
                            id="reputation-da"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="reputation-da"
                          >
                            Da
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="false"
                            id="reputation-nu"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="reputation-nu"
                          >
                            Nu
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="damages_state_reputation" />
                      {impactData.section2_public_impact.defense_security
                        .damages_state_reputation === true && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section2_public_impact.defense_security
                                  .damages_state_reputation_explanation || ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section2_public_impact",
                                  "defense_security",
                                  "damages_state_reputation_explanation",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "damages_state_reputation_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="damages_state_reputation_explanation" />
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Section 3: Systemic Risk (9c) */}
        <Card className="border-destructive/20">
          <Collapsible
            open={sectionStates.section3}
            onOpenChange={() => toggleSection("section3")}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-destructive/5">
                <CardTitle className="text-sm md:text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    Secțiunea 3: Evaluarea aplicabilității art. 9 lit. c)
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${sectionStates.section3 ? "rotate-180" : ""}`}
                  />
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  În vederea determinării categoriei de impact pe care entitatea
                  îl poate genera, aceasta trebuie să ia în considerare efectul
                  potențial maxim al compromiterii, blocării ori distrugerii
                  complete a datelor, serviciului sau infrastructurii entității.
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="py-3 space-y-4 md:py-3">
                <div className="space-y-4">
                  <h4 className="font-heading text-sm md:text-base font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Impactul transsectorial sau transfrontalier
                  </h4>
                  <div className="space-y-3 ml-6">
                    <Label className="text-sm md:text-base font-medium">
                      1. Pot fi afectate alte servicii esențiale din sectoare
                      diferite?
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <RadioGroup
                      value={
                        impactData.section3_systemic_risk.affects_other_sectors?.toString() ??
                        ""
                      }
                      onValueChange={(value) =>
                        handleRadioChange(
                          "section3_systemic_risk",
                          "",
                          "affects_other_sectors",
                          value === "true",
                          [
                            "affected_sectors_count",
                            "highest_impact_percentage",
                            "highest_impact_percentage_explanation",
                          ]
                        )
                      }
                      className={cn(
                        fieldErrors["affects_other_sectors"]
                          ? "border border-destructive rounded-md p-2"
                          : "",
                        "text-sm md:text-base"
                      )}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="true"
                          id="sectors-da"
                          variant="checkbox"
                        />
                        <Label className="cursor-pointer" htmlFor="sectors-da">
                          Da
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="false"
                          id="sectors-nu"
                          variant="checkbox"
                        />
                        <Label className="cursor-pointer" htmlFor="sectors-nu">
                          Nu
                        </Label>
                      </div>
                    </RadioGroup>
                    <FieldError fieldName="affects_other_sectors" />
                    {impactData.section3_systemic_risk.affects_other_sectors ===
                      true && (
                        <div className="space-y-2">
                          <Label className="text-sm md:text-base">
                            Explicație{" "}
                            <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Textarea
                            placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                            value={
                              impactData.section3_systemic_risk
                                .affects_other_sectors_explanation || ""
                            }
                            onChange={(e) =>
                              handleTextareaChange(
                                "section3_systemic_risk",
                                "",
                                "affects_other_sectors_explanation",
                                e.target.value
                              )
                            }
                            className={cn(
                              fieldErrors["affects_other_sectors_explanation"]
                                ? "border-destructive"
                                : "",
                              "text-sm md:text-base"
                            )}
                          />
                          <FieldError fieldName="affects_other_sectors_explanation" />
                        </div>
                      )}

                    {impactData.section3_systemic_risk
                      .affects_other_sectors && (
                        <div className="ml-4 space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Dacă da: Cate sectoare?
                            </Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="Inserați valoarea"
                              value={
                                impactData.section3_systemic_risk
                                  .affected_sectors_count ?? ""
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                if (
                                  inputValue === "" ||
                                  inputValue === "0" ||
                                  (parseInt(inputValue) >= 0 &&
                                    !isNaN(parseInt(inputValue)))
                                ) {
                                  const numValue =
                                    inputValue === "" ? 0 : parseInt(inputValue);
                                  setImpactData((prev) => ({
                                    ...prev,
                                    section3_systemic_risk: {
                                      ...prev.section3_systemic_risk,
                                      affected_sectors_count: numValue,
                                    },
                                  }));

                                  // Clear field error when user provides input
                                  if (fieldErrors["affected_sectors_count"]) {
                                    setFieldErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors["affected_sectors_count"];
                                      return newErrors;
                                    });
                                  }
                                }
                              }}
                              className={cn(
                                fieldErrors["affected_sectors_count"]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Se va insera o valoare numerică fără spații sau
                              puncte de delimitare a miilor, milioanelor etc. Dacă
                              nu se aplică entității introduceți „0"
                            </p>
                            <FieldError fieldName="affected_sectors_count" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Care este procentul cel mai mare de afectare (cotă
                              de piață)?
                            </Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="Inserați valoarea"
                              value={
                                impactData.section3_systemic_risk
                                  .highest_impact_percentage ?? ""
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                // Allow empty string or valid numbers 0-100
                                if (
                                  inputValue === "" ||
                                  inputValue === "0" ||
                                  (parseInt(inputValue, 10) >= 0 &&
                                    parseInt(inputValue, 10) <= 100 &&
                                    !Number.isNaN(parseInt(inputValue, 10)))
                                ) {
                                  const numValue =
                                    inputValue === ""
                                      ? 0
                                      : parseInt(inputValue, 10);
                                  setImpactData((prev) => ({
                                    ...prev,
                                    section3_systemic_risk: {
                                      ...prev.section3_systemic_risk,
                                      highest_impact_percentage: numValue,
                                      // Clear explanation when value is 0
                                      ...(numValue === 0 && {
                                        highest_impact_percentage_explanation: "",
                                      }),
                                    },
                                  }));

                                  // Clear field error when user provides input
                                  if (fieldErrors.highest_impact_percentage) {
                                    setFieldErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors.highest_impact_percentage;
                                      return newErrors;
                                    });
                                  }
                                }
                              }}
                              className={cn(
                                fieldErrors.highest_impact_percentage
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Se va insera o valoare numerică fără spații sau
                              puncte de delimitare a miilor, milioanelor etc. Dacă
                              nu se aplică entității introduceți „0"
                            </p>
                            <FieldError fieldName="highest_impact_percentage" />
                            {impactData.section3_systemic_risk
                              .highest_impact_percentage !== null &&
                              impactData.section3_systemic_risk
                                .highest_impact_percentage > 0 && (
                                <div className="space-y-2">
                                  <Label className="text-sm md:text-base">
                                    Explicație{" "}
                                    <span className="text-destructive ml-1">
                                      *
                                    </span>
                                  </Label>
                                  <Textarea
                                    placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați introdus o valoare diferită de 0):"
                                    value={
                                      impactData.section3_systemic_risk
                                        .highest_impact_percentage_explanation ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      handleTextareaChange(
                                        "section3_systemic_risk",
                                        "",
                                        "highest_impact_percentage_explanation",
                                        e.target.value
                                      )
                                    }
                                    className={cn(
                                      fieldErrors[
                                        "highest_sector_impact_percentage_explanation"
                                      ]
                                        ? "border-destructive"
                                        : "",
                                      "text-sm md:text-base"
                                    )}
                                  />
                                  <FieldError fieldName="highest_impact_percentage_explanation" />
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    <Separator />

                    <div className="space-y-3">
                      <Label className="text-sm md:text-base font-medium">
                        2. Poate fi afectată capacitatea României de a îndeplini
                        obligațiile ce decurg din dreptul internațional și
                        cadrele multilaterale?
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <RadioGroup
                        value={
                          impactData.section3_systemic_risk
                            .affects_international_obligations ?? ""
                        }
                        onValueChange={(
                          value: InternationalObligationsImpact
                        ) =>
                          handleRadioChange(
                            "section3_systemic_risk",
                            "",
                            "affects_international_obligations",
                            value
                          )
                        }
                        className={cn(
                          fieldErrors.affects_international_obligations
                            ? "border border-destructive rounded-md p-2"
                            : "",
                          "text-sm md:text-base"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="no"
                            id="intl-no"
                            variant="checkbox"
                          />
                          <Label className="cursor-pointer" htmlFor="intl-no">
                            Nu
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="affected"
                            id="intl-affected"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="intl-affected"
                          >
                            Da, poate fi afectată
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="destroyed"
                            id="intl-destroyed"
                            variant="checkbox"
                          />
                          <Label
                            className="cursor-pointer"
                            htmlFor="intl-destroyed"
                          >
                            Da, pot rezulta distrugeri
                          </Label>
                        </div>
                      </RadioGroup>
                      <FieldError fieldName="affects_international_obligations" />
                      {(impactData.section3_systemic_risk
                        .affects_international_obligations === "affected" ||
                        impactData.section3_systemic_risk
                          .affects_international_obligations ===
                        "destroyed") && (
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">
                              Explicație{" "}
                              <span className="text-destructive ml-1">*</span>
                            </Label>
                            <Textarea
                              placeholder="Explicații, date relevante, argumente (câmp obligatoriu dacă ați selectat 0):"
                              value={
                                impactData.section3_systemic_risk
                                  .affects_international_obligations_explanation ||
                                ""
                              }
                              onChange={(e) =>
                                handleTextareaChange(
                                  "section3_systemic_risk",
                                  "",
                                  "affects_international_obligations",
                                  e.target.value
                                )
                              }
                              className={cn(
                                fieldErrors[
                                  "affects_international_obligations_explanation"
                                ]
                                  ? "border-destructive"
                                  : "",
                                "text-sm md:text-base"
                              )}
                            />
                            <FieldError fieldName="affects_international_obligations_explanation" />
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>

      <ImpactResultsTable impactData={impactData} />

      <LegalRepresentativeForm
        data={reprezentantData}
        onDataChange={handleReprezentantChange}
        fieldErrors={fieldErrors}
      />

      {/* Actions */}
      <Card>
        <CardContent className="pt-6 md:pt-6">
          <div className="flex flex-wrap gap-4">
            <PaywallButton
              locked={!!hasInsufficientClassifCredits}
              leftIcon={<Scale className="h-4 w-4 mr-2" />}
              onClick={processAnalysis}
              className="flex-1"
              disabled={isProcessing}
            >
              Finalizează Analiza de Impact
            </PaywallButton>
            {onDownloadPdf && (
              <PaywallButton
                locked={!!hasInsufficientCredits && !(isProcessing || isLoadingCredits)}
                leftIcon={
                  isProcessing || isLoadingCredits ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )
                }
                onClick={processDownloadPdf}
                className="flex-1"
                variant="outline"
                disabled={isProcessing || isLoadingCredits}
              >
                Descarcă PDF
              </PaywallButton>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Completați secțiunile relevante pentru entitatea dumneavoastră
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
